import { z } from 'zod'
import type { FileSelectionPrecondition, FolderSelectionPrecondition } from '@/user_files/schemas/preconditions'

// =============================================================================
// Request Types (TypeScript only)
// =============================================================================

/** Mirrors `DeleteSelectionMode` in paxton_app/user_files/delete/schemas.py. */
export enum DeleteSelectionMode {
  /** Delete every file covered by the selection, including files in use by a matter record. */
  All = 'all',
  /** Retain files in use by a matter record, plus the selected folders their paths need. */
  NotInUse = 'not_in_use',
}

export type DeleteSelectionRequest = {
  file_ids?: string[]
  folder_ids?: string[]
  file_preconditions?: FileSelectionPrecondition[]
  folder_preconditions?: FolderSelectionPrecondition[]
  mode?: DeleteSelectionMode
}

export type DeletePreviewRequest = {
  file_ids?: string[]
  folder_ids?: string[]
}

// =============================================================================
// Response Schemas (Zod - runtime validated)
// =============================================================================

export const DeleteSelectionResponseSchema = z.object({
  deleted_file_ids: z.array(z.string().uuid()),
  deleted_folder_ids: z.array(z.string().uuid()),
  directly_deleted_file_ids: z.array(z.string().uuid()),
  directly_deleted_folder_ids: z.array(z.string().uuid()),
  unchanged_file_ids: z.array(z.string().uuid()),
  unchanged_folder_ids: z.array(z.string().uuid()),
})

export type DeleteSelectionResponse = z.infer<typeof DeleteSelectionResponseSchema>

/** Matter record type that can hold a file in use. Mirrors `DeletePreviewUsageType` on the backend. */
export enum DeletePreviewUsageType {
  BillingSummary = 'billing_summary',
  MedChron = 'medchron',
  Timeline = 'timeline',
}

const DeletePreviewFileUsageSchema = z.object({
  // Deliberately not a native enum: a record type this build has never heard of must not fail the
  // whole response and take the delete warning down with it. Unknown types still mark the file as
  // in use; the dialog just cannot deep-link them.
  type: z.string(),
  matter_id: z.string().uuid(),
})

const DeletePreviewInUseFileSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Root-relative folder names leading to the file, so nested files stay distinguishable. */
  display_path: z.array(z.string()),
  /** A file can be in use by more than one matter record, and by another matter's record. */
  usages: z.array(DeletePreviewFileUsageSchema),
})

export const DeletePreviewResponseSchema = z.object({
  in_use_files: z.array(DeletePreviewInUseFileSchema),
  delete_all_file_count: z.number().int().nonnegative(),
  delete_not_in_use_file_count: z.number().int().nonnegative(),
})

export type DeletePreviewFileUsage = z.infer<typeof DeletePreviewFileUsageSchema>
export type DeletePreviewInUseFile = z.infer<typeof DeletePreviewInUseFileSchema>
export type DeletePreviewResponse = z.infer<typeof DeletePreviewResponseSchema>
